import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import { find, join, propEq } from 'ramda';
import cx from 'classnames';
import moment from 'moment';
import Avatar from '@components/@core/Avatar';
import BlogAdvancedSearch from '@components/BlogAdvancedSearch';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import Pagination from '@components/@core/Pagination';
import Thumbnail from '@components/@core/Thumbnail';
import { getImageURL } from '~/services/util';

const Header = () => (
    <Row className="mx-0 border-bottom d-none d-sm-flex">
        <Col xs={1} className="px-0">
            <p className="dsl-m16 my-2">Image</p>
        </Col>
        <Col xs={3}>
            <p className="dsl-m16 my-2">Title</p>
        </Col>
        <Col xs={3} className="pl-2">
            <p className="dsl-m16 my-2">Created</p>
        </Col>
        <Col xs={3} className="pl-2">
            <p className="dsl-m16 my-2">Categories</p>
        </Col>
        <Col xs={2} className="pl-2">
            <p className="dsl-m16 my-2">Author</p>
        </Col>
    </Row>
);

const Blog = ({ data, author, editable, onEdit, onDetail }) => {
    const categories = data?.data?.blog_categories || [];
    const categoryNames = categories.length > 0 ? join(', ', categories) : 'Na';
    const url = getImageURL(data.data?.body);
    return (
        <Row className="mx-0 py-3 border-bottom flex-column flex-sm-row" onClick={onDetail}>
            <Col xs={12} sm={1} className="px-0">
                <Thumbnail className="responsive-thumbnail" size="responsive" source={url} />
            </Col>
            <Col xs={12} sm={2} className="d-flex d-sm-none d-h-between pe-0 ps-0 ps-md-2 my-2 my-md-0">
                <div className="d-h-center flex-row flex-sm-column">
                    <Avatar type="logo" className="mr-2 me-sm-0" url={data?.author_avatar} isSponsored={true} />
                    <p className="dsl-b16 text-400 mt-1 mb-0">{author?.first_name}</p>
                </div>
                {editable && <EditDropdown options={['Add', 'Delete', 'Edit']} onChange={onEdit} />}
            </Col>
            <Col xs={12} sm={3} className="d-flex align-items-center d-sm-block px-0 px-md-2">
                <label className="d-block d-sm-none mb-0 me-2 title-w">Title</label>
                <p className="dsl-b16 truncate-two text-400 mb-0 pt-0 pt-md-1 my-2 my-md-0 w-sm-content">{data?.name}</p>
            </Col>
            <Col xs={12} sm={3} className="pl-sm-0 d-flex d-sm-block px-0 px-md-2">
                <label className="d-block d-sm-none mb-0 me-2 title-w">Created</label>
                <p className="dsl-b16 mb-0 my-2 my-md-0">{moment(data?.created_at).format('MMM DD, YY')}</p>
            </Col>
            <Col xs={12} sm={3} className="pr-sm-0 d-flex align-items-center d-sm-block px-0 px-md-2">
                <label className="d-block d-sm-none mb-0 me-2 title-w">Categories</label>
                <p className="dsl-b16 truncate-two mb-0 my-2 my-md-0 w-sm-content">{categoryNames}</p>
            </Col>
            <Col xs={12} sm={2} className="d-h-between pe-0 ps-0 ps-md-2 my-2 my-md-0 d-none d-sm-flex">
                <div className="d-h-center flex-row flex-sm-column">
                    <Avatar type="logo" className="mr-2 me-sm-0" url={data?.author_avatar} isSponsored={true} />
                    <p className="dsl-b16 mt-1 mb-0">{author?.first_name}</p>
                </div>
                {editable && <EditDropdown options={['Add', 'Delete', 'Edit']} onChange={onEdit} />}
            </Col>
        </Row>
    );
};

const Drafts = ({ data, writers, editable, authors, categories, departments, search, onSelect, onSearch, onDetail }) => {
    const handleEdit = (data) => (e) => {
        onSelect(e, data);
    };

    const handleCancelSearchTerm = (type) => () => {
        onSearch({
            search: type !== 'search' ? search?.search : null,
            author: search?.author && type !== 'author' ? [search.author.id] : null,
            blogCategory: search?.category && type !== 'category' ? [search.category] : null,
            categoryId: search?.department && type !== 'department' ? [search.department.id] : null,
        });
    };

    const page = data?.current_page || 1;
    const total = data?.total;
    const emptySearchTerms = (!search?.search || search?.search === '') && !search?.author && !search?.category && !search?.department;

    return (
        <div className="blog-admin-drafts">
            {!emptySearchTerms ? (
                <>
                    <p className="dsl-b20 bold pt-3 mb-1">Search Results</p>
                    <div className="d-h-start py-2">
                        {search.author && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('author')}>
                                <span className="dsl-b14">{`${search.author.first_name} ${search.author.last_name}`}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {search.search && search.search !== '' && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('search')}>
                                <span className="dsl-b14">{search.search}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {search.category && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('category')}>
                                <span className="dsl-b14">{search.category}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {search.department && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('department')}>
                                <span className="dsl-b14">{search.department.name}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="d-h-end py-2">
                    <BlogAdvancedSearch authors={authors} categories={categories} departments={departments} onSearch={onSearch} />
                    <Icon name="fas fa-chart-pie-alt" size={14} />
                </div>
            )}
            {total > 0 && <Header />}
            {data?.data?.map((item) => {
                const author = item.author || find(propEq('id', item.data?.writer_id), writers);
                return <Blog key={`draft-${item.id}`} data={item} author={author} editable={editable} onEdit={handleEdit(item)} onDetail={onDetail(item)} />;
            })}
            <div className={cx('flex-column flex-sm-row mt-3', total > data?.per_page ? 'd-h-between' : 'd-h-end')}>
                {total > 0 && <Pagination total={data?.last_page} current={page} pers={[]} />}
                {editable && <Button type="high" name="ADD POST" onClick={() => onSelect('add')} />}
            </div>
        </div>
    );
};

Drafts.propTypes = {
    data: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        total: PropTypes.number,
    }),
    writers: PropTypes.array,
    editable: PropTypes.bool,
    search: PropTypes.shape({
        search: PropTypes.string,
        author: PropTypes.any,
        category: PropTypes.string,
        department: PropTypes.any,
    }),
    onSearch: PropTypes.func,
    onSelect: PropTypes.func,
};

Drafts.defaultProps = {
    data: {
        current_page: 0,
        data: [],
        last_page: 0,
        total: 0,
    },
    writers: [],
    editable: false,
    search: {
        search: '',
        author: null,
        category: null,
        department: null,
    },
    onSearch: () => {},
    onSelect: () => {},
};

export default Drafts;
